import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { ErrorBoundary } from './ErrorBoundary';
import { openServerPdf } from '../utils/pdfPrint';
import { AuthContext } from '../shared';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Users, Plus, Search, Edit, Trash2, AlertTriangle, Building2, Plane, BedDouble, BarChart3, ArrowRightLeft, UserPlus, RefreshCw, Check, X, Printer, FileText } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

// ============ MAIN COMPONENT ============
const UmrahGroupManagement = ({ setActiveTab: setRootActiveTab } = {}) => {
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'super_admin' || user?.role === 'general_accountant' || user?.role === 'general_manager' || user?.role === 'services_manager';
  const isSuperAdmin = user?.role === 'super_admin';
  const [activeTab, setActiveTab] = useState('dashboard');
  const [trips, setTrips] = useState([]);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dashboardStats, setDashboardStats] = useState(null);
  const [flightLinks, setFlightLinks] = useState({}); // 🆕 { trip_id: flight }

  const fetchTrips = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/umrah/trips`, { headers: getAuthHeaders() });
      setTrips(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchAgencies = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/agencies`, { headers: getAuthHeaders() });
      setAgencies(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchDashboardStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/umrah/dashboard-stats`, { headers: getAuthHeaders() });
      setDashboardStats(res.data);
    } catch (e) { console.error(e); }
  }, []);

  const fetchFlightLinks = useCallback(async () => {
    if (!isAdmin) { setFlightLinks({}); return; }
    try {
      const res = await axios.get(`${API}/flights/meta/links-map`, { headers: getAuthHeaders() });
      setFlightLinks(res.data || {});
    } catch (e) { /* silent — endpoint may be inaccessible for some roles */ }
  }, [isAdmin]);

  useEffect(() => {
    fetchTrips();
    fetchAgencies();
    fetchDashboardStats();
    fetchFlightLinks();
  }, [fetchTrips, fetchAgencies, fetchDashboardStats, fetchFlightLinks]);

  return (
    <div className="space-y-6" data-testid="umrah-management">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">🛫 إدارة الأفواج (حج • عمرة • سياحية • أخرى)</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-6 w-full">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">
            <BarChart3 className="h-4 w-4 ml-2" /> لوحة التحكم
          </TabsTrigger>
          <TabsTrigger value="trips" data-testid="tab-trips">
            <Plane className="h-4 w-4 ml-2" /> الأفواج
          </TabsTrigger>
          <TabsTrigger value="pilgrims" data-testid="tab-pilgrims">
            <Users className="h-4 w-4 ml-2" /> الحجاج
          </TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">
            <FileText className="h-4 w-4 ml-2" /> الرحلات المنظمة
          </TabsTrigger>
          <TabsTrigger value="rooms" data-testid="tab-rooms">
            <BedDouble className="h-4 w-4 ml-2" /> التسكين
          </TabsTrigger>
          <TabsTrigger value="logs" data-testid="tab-logs">
            <RefreshCw className="h-4 w-4 ml-2" /> السجلات
          </TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <DashboardTab stats={dashboardStats} trips={trips} onRefresh={fetchDashboardStats} />
        </TabsContent>
        <TabsContent value="trips">
          <TripsTab trips={trips} agencies={agencies} onRefresh={fetchTrips} 
                    selectedTrip={selectedTrip} setSelectedTrip={setSelectedTrip}
                    flightLinks={flightLinks} setRootActiveTab={setRootActiveTab}
                    isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="pilgrims">
          <PilgrimsTab trips={trips} agencies={agencies} selectedTrip={selectedTrip} 
                       setSelectedTrip={setSelectedTrip} onRefresh={fetchTrips} />
        </TabsContent>
        <TabsContent value="bookings">
          <BookingsTab trips={trips} selectedTrip={selectedTrip} setSelectedTrip={setSelectedTrip} />
        </TabsContent>
        <TabsContent value="rooms">
          <RoomsTab trips={trips} selectedTrip={selectedTrip} setSelectedTrip={setSelectedTrip} />
        </TabsContent>
        <TabsContent value="logs">
          <LogsTab trips={trips} selectedTrip={selectedTrip} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// ============ DASHBOARD TAB ============
const DashboardTab = ({ stats, trips, onRefresh }) => {
  if (!stats) return <div className="text-center py-8">جاري التحميل...</div>;

  return (
    <div className="space-y-6 mt-4">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-r-4 border-r-brand-500">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">إجمالي الأفواج</div>
            <div className="text-3xl font-bold text-brand-600">{stats.total_trips}</div>
            <div className="text-xs text-green-600">{stats.open_trips} مفتوح</div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-green-500">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">إجمالي الحجاج</div>
            <div className="text-3xl font-bold text-green-600">{stats.total_pilgrims}</div>
            <div className="text-xs text-gray-500">
              ذكور: {stats.male_count} | إناث: {stats.female_count}
            </div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-purple-500">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">التصنيفات</div>
            <div className="text-lg font-bold">
              <span className="text-purple-600">ADT: {stats.adt_count}</span>
            </div>
            <div className="text-xs text-gray-500">
              CHD: {stats.chd_count} | INF: {stats.inf_count}
            </div>
          </CardContent>
        </Card>
        <Card className="border-r-4 border-r-orange-500">
          <CardContent className="p-4">
            <div className="text-sm text-gray-500">الإعاشة</div>
            <div className="text-lg font-bold">
              <span className="text-green-600">{stats.with_meals || 0}</span>
              <span className="text-gray-400 mx-1">/</span>
              <span className="text-red-500">{stats.without_meals || 0}</span>
            </div>
            <div className="text-xs text-gray-500">
              بالإعاشة / بدون إعاشة
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {stats.alerts?.length > 0 && (
        <Card className="border-yellow-300 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-600" />
              التنبيهات
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {stats.alerts.map((alert, i) => (
                <div key={i} className="flex items-center gap-2 text-sm">
                  <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
                    {alert.type === 'capacity_warning' ? 'سعة' : 'غرف'}
                  </Badge>
                  {alert.type === 'capacity_warning' 
                    ? `الفوج "${alert.trip}" وصل ${alert.usage}% من السعة`
                    : `${alert.count} غرفة غير مكتملة`}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Agency Stats */}
      {stats.agency_stats?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">إحصائيات الوكالات</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الوكالة</TableHead>
                  <TableHead className="text-right">عدد الحجاج</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats.agency_stats.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.agency_name}</TableCell>
                    <TableCell>
                      <Badge>{a.pilgrim_count}</Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ TRIPS TAB ============
const TripsTab = ({ trips, agencies, onRefresh, selectedTrip, setSelectedTrip, flightLinks = {}, setRootActiveTab, isAdmin = false }) => {
  const [showCreate, setShowCreate] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [allocateTrip, setAllocateTrip] = useState(null);
  const [formData, setFormData] = useState({ name: '', departure_date: '', return_date: '', total_seats: '', description: '', trip_type: 'عمرة', base_price: '' });

  const createTrip = async () => {
    try {
      await axios.post(`${API}/umrah/trips`, {
        ...formData,
        total_seats: parseInt(formData.total_seats),
        base_price: formData.base_price ? parseFloat(formData.base_price) : 0
      }, { headers: getAuthHeaders() });
      setShowCreate(false);
      setFormData({ name: '', departure_date: '', return_date: '', total_seats: '', description: '', trip_type: 'عمرة', base_price: '' });
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في إنشاء الفوج');
    }
  };

  const deleteTrip = async (tripId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الفوج؟')) return;
    try {
      await axios.delete(`${API}/umrah/trips/${tripId}`, { headers: getAuthHeaders() });
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في حذف الفوج');
    }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">إدارة الأفواج</h2>
        <Button onClick={() => setShowCreate(true)} data-testid="create-trip-btn">
          <Plus className="h-4 w-4 ml-2" /> إنشاء فوج جديد
        </Button>
      </div>

      {/* Trips List */}
      <div className="grid gap-4">
        {trips.map(trip => (
          <Card key={trip.id} className={`cursor-pointer hover:shadow-md transition-shadow ${selectedTrip?.id === trip.id ? 'ring-2 ring-brand-500' : ''}`}
                onClick={() => setSelectedTrip(trip)}>
            <CardContent className="p-4">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="font-semibold text-lg">{trip.name}</h3>
                  <div className="text-sm text-gray-500 mt-1">
                    المغادرة: {trip.departure_date} 
                    {trip.return_date && ` | العودة: ${trip.return_date}`}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-brand-600">
                      {trip.pilgrim_count || 0}/{trip.total_seats}
                    </div>
                    <div className="text-xs text-gray-500">مقعد</div>
                    <div className="w-32 h-2 bg-gray-200 rounded-full mt-1">
                      <div className="h-full bg-brand-500 rounded-full" 
                           style={{width: `${Math.min(100, ((trip.pilgrim_count || 0) / trip.total_seats) * 100)}%`}} />
                    </div>
                  </div>
                  <Badge variant={trip.status === 'open' ? 'default' : trip.status === 'full' ? 'destructive' : 'secondary'}>
                    {trip.status === 'open' ? 'مفتوح' : trip.status === 'full' ? 'ممتلئ' : 'مغلق'}
                  </Badge>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setAllocateTrip(trip); setShowAllocate(true); }}>
                      <Building2 className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="destructive" onClick={(e) => { e.stopPropagation(); deleteTrip(trip.id); }}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              {/* Allocations */}
              {trip.allocations?.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {trip.allocations.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {a.agency_name}: {a.used_seats || 0}/{a.allocated_seats}
                    </Badge>
                  ))}
                </div>
              )}
              {/* 🆕 الرحلة الجوية المرتبطة — للمدير العام والمحاسب العام فقط */}
              {isAdmin && (() => {
                const fl = flightLinks?.[trip.id];
                if (fl) {
                  return (
                    <div
                      className="mt-3 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-3 py-2 cursor-pointer hover:bg-blue-100 transition"
                      onClick={(e) => {
                        e.stopPropagation();
                        sessionStorage.setItem('pending-open-flight-id', fl.id);
                        if (setRootActiveTab) setRootActiveTab('flights-management');
                      }}
                      data-testid={`trip-flight-badge-${trip.id}`}
                      title="افتح الرحلة الجوية المرتبطة"
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <Plane className="h-4 w-4 text-blue-700" />
                        <span className="font-bold text-blue-900">{fl.flight_no}</span>
                        <span className="text-blue-700">•</span>
                        <span className="text-blue-800">{fl.airline_name}</span>
                        <span className="text-blue-700">•</span>
                        <span className="text-xs text-blue-700">{fl.departure_date}</span>
                        {fl.return_date && (
                          <>
                            <span className="text-blue-700">→</span>
                            <span className="text-xs text-blue-700">{fl.return_date}</span>
                          </>
                        )}
                      </div>
                      <Badge className="bg-blue-600 text-white text-xs">↗ افتح</Badge>
                    </div>
                  );
                }
                return (
                  <div className="mt-3 flex items-center justify-between bg-gray-50 border border-dashed border-gray-300 rounded-md px-3 py-2">
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      <Plane className="h-3 w-3" /> لا توجد رحلة جوية مرتبطة بهذا الفوج
                    </div>
                    {setRootActiveTab && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-xs h-7"
                        onClick={(e) => {
                          e.stopPropagation();
                          sessionStorage.setItem('pending-create-flight-trip-id', trip.id);
                          setRootActiveTab('flights-management');
                        }}
                        data-testid={`trip-create-flight-${trip.id}`}
                      >
                        📎 إنشاء رحلة جوية
                      </Button>
                    )}
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        ))}
        {trips.length === 0 && (
          <div className="text-center py-8 text-gray-500">لا توجد أفواج بعد</div>
        )}
      </div>

      {/* Create Trip Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>إنشاء فوج جديد</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>اسم الفوج</Label>
              <Input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} placeholder="مثلاً: فوج عمرة رجب 2026، رحلة سياحية فبراير..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>نوع الفوج</Label>
                <select
                  value={formData.trip_type}
                  onChange={e => setFormData({...formData, trip_type: e.target.value})}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500"
                  data-testid="trip-type-select"
                >
                  <option value="حج">🕋 حج</option>
                  <option value="عمرة">🕋 عمرة</option>
                  <option value="سياحية">🌴 رحلة سياحية</option>
                  <option value="أخرى">✈️ أخرى</option>
                </select>
              </div>
              <div>
                <Label>السعر الموحد للفوج (دج، اختياري)</Label>
                <Input type="number" value={formData.base_price} onChange={e => setFormData({...formData, base_price: e.target.value})} placeholder="200000" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>تاريخ المغادرة</Label>
                <Input type="date" value={formData.departure_date} onChange={e => setFormData({...formData, departure_date: e.target.value})} />
              </div>
              <div>
                <Label>تاريخ العودة</Label>
                <Input type="date" value={formData.return_date} onChange={e => setFormData({...formData, return_date: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>عدد المقاعد الإجمالي</Label>
              <Input type="number" value={formData.total_seats} onChange={e => setFormData({...formData, total_seats: e.target.value})} />
            </div>
            <div>
              <Label>وصف (اختياري)</Label>
              <Input value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} />
            </div>
            <Button onClick={createTrip} className="w-full" data-testid="submit-create-trip">إنشاء الفوج</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Seat Allocation Dialog */}
      <SeatAllocationDialog open={showAllocate} onClose={() => setShowAllocate(false)} 
                            trip={allocateTrip} agencies={agencies} onRefresh={onRefresh} />
    </div>
  );
};

// ============ SEAT ALLOCATION DIALOG ============
const SeatAllocationDialog = ({ open, onClose, trip, agencies, onRefresh }) => {
  const [selectedAgency, setSelectedAgency] = useState('');
  const [seats, setSeats] = useState('');
  const [seatSummary, setSeatSummary] = useState(null);

  useEffect(() => {
    if (open && trip) {
      fetchSeatSummary();
    }
  }, [open, trip]);

  const fetchSeatSummary = async () => {
    if (!trip) return;
    try {
      const res = await axios.get(`${API}/umrah/trips/${trip.id}/seat-summary`, { headers: getAuthHeaders() });
      setSeatSummary(res.data);
    } catch (e) { console.error(e); }
  };

  const allocateSeats = async () => {
    if (!selectedAgency || !seats) return;
    const agency = agencies.find(a => a.id === selectedAgency);
    try {
      await axios.post(`${API}/umrah/trips/${trip.id}/allocate-seats`, {
        agency_id: selectedAgency,
        agency_name: agency?.name || '',
        allocated_seats: parseInt(seats)
      }, { headers: getAuthHeaders() });
      setSelectedAgency('');
      setSeats('');
      fetchSeatSummary();
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في تخصيص المقاعد');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>توزيع المقاعد - {trip?.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {seatSummary && (
            <div className="bg-brand-50 p-3 rounded-lg text-sm">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div><span className="font-bold text-brand-600">{seatSummary.total_seats}</span><br/>إجمالي</div>
                <div><span className="font-bold text-green-600">{seatSummary.total_allocated}</span><br/>موزع</div>
                <div><span className="font-bold text-orange-600">{seatSummary.unallocated_seats}</span><br/>متبقي</div>
              </div>
            </div>
          )}

          {seatSummary?.allocations?.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-right">الوكالة</TableHead>
                  <TableHead className="text-right">مخصص</TableHead>
                  <TableHead className="text-right">مستخدم</TableHead>
                  <TableHead className="text-right">متبقي</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {seatSummary.allocations.map((a, i) => (
                  <TableRow key={i}>
                    <TableCell>{a.agency_name}</TableCell>
                    <TableCell>{a.allocated_seats}</TableCell>
                    <TableCell>{a.used_seats}</TableCell>
                    <TableCell className={a.remaining_seats <= 0 ? 'text-red-600 font-bold' : 'text-green-600'}>
                      {a.remaining_seats}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}

          <div className="border-t pt-4">
            <h4 className="font-medium mb-2">تخصيص جديد</h4>
            <div className="grid grid-cols-3 gap-2">
              <Select value={selectedAgency} onValueChange={setSelectedAgency}>
                <SelectTrigger><SelectValue placeholder="الوكالة" /></SelectTrigger>
                <SelectContent>
                  {agencies.map(a => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Input type="number" placeholder="عدد المقاعد" value={seats} onChange={e => setSeats(e.target.value)} />
              <Button onClick={allocateSeats}>تخصيص</Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ PILGRIMS TAB ============
// 🆕 Mirror of backend ``calculate_category`` (thresholds: INF<2, CHD<12, ADT≥12).
// Used by the bed-status block to know which fields to show/lock.
const computeCategory = (birthDateStr) => {
  if (!birthDateStr) return null;
  try {
    const b = new Date(birthDateStr);
    if (isNaN(b.getTime())) return null;
    const today = new Date();
    const ageYears = (today - b) / (1000 * 60 * 60 * 24 * 365.25);
    if (ageYears < 2) return 'INF';
    if (ageYears < 12) return 'CHD';
    return 'ADT';
  } catch { return null; }
};

const PilgrimsTab = ({ trips, agencies, selectedTrip, setSelectedTrip, onRefresh }) => {
  const [pilgrims, setPilgrims] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showFamily, setShowFamily] = useState(false);
  const [showTransfer, setShowTransfer] = useState(false);
  const [transferPilgrim, setTransferPilgrim] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editPilgrim, setEditPilgrim] = useState(null);
  const [filters, setFilters] = useState({ gender: '', category: '', status: '', agency_id: '', search: '', has_meals: '' });
  const [formData, setFormData] = useState({
    first_name: '', last_name: '', passport_number: '', birth_date: '',
    gender: '', status: 'confirmed', agency_id: '', phone: '', notes: '',
    has_meals: true,
    family_id: '', family_name: '', is_family_leader: false,
    with_bed: true, // 🆕 يُطبّق على الأطفال فقط (ADT/INF محسومان تلقائيًا في الـ backend)
    crew_role: '', // 🆕 دور الطاقم — عند تحديده يصبح المعتمر طاقماً مجانياً في الرحلة الجوية
    discount_category: '', // 🆕 فئة المستفيد من تخفيض الطيران (مجاهد/ابن شهيد/أخرى)
  });
  const [families, setFamilies] = useState([]);
  // CRM Integration: existing clients for autofill
  const [clients, setClients] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [showClientList, setShowClientList] = useState(false);
  const [linkedClientId, setLinkedClientId] = useState(null);

  const fetchClients = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients`, { headers: getAuthHeaders() });
      setClients(res.data || []);
    } catch (e) { /* clients optional */ }
  }, []);

  const fetchPilgrims = useCallback(async () => {
    if (!selectedTrip) return;
    try {
      const params = new URLSearchParams({ trip_id: selectedTrip.id });
      Object.entries(filters).forEach(([k, v]) => { if (v) params.append(k, v); });
      const res = await axios.get(`${API}/umrah/pilgrims?${params}`, { headers: getAuthHeaders() });
      let rows = res.data || [];
      // 🆕 Enrich with inherited discount_category from the linked organized_trip
      // (so the badge shows even if the pilgrim record itself doesn't carry it)
      try {
        const otRes = await axios.get(`${API}/organized-trips?umrah_trip_id=${selectedTrip.id}&limit=500`, { headers: getAuthHeaders() });
        const trips = Array.isArray(otRes.data) ? otRes.data : (otRes.data?.trips || []);
        const byClient = {};
        for (const t of trips) {
          if (t.client_id && t.discount_category) byClient[t.client_id] = t.discount_category;
        }
        rows = rows.map(p => (!p.discount_category && p.client_id && byClient[p.client_id])
          ? { ...p, discount_category: byClient[p.client_id], _inherited_discount: true }
          : p
        );
      } catch (_) { /* silent — not critical */ }
      setPilgrims(rows);
    } catch (e) { console.error(e); }
  }, [selectedTrip, filters]);

  const fetchFamilies = useCallback(async () => {
    if (!selectedTrip) return;
    try {
      const res = await axios.get(`${API}/umrah/families?trip_id=${selectedTrip.id}`, { headers: getAuthHeaders() });
      setFamilies(res.data);
    } catch (e) { console.error(e); }
  }, [selectedTrip]);

  useEffect(() => { fetchPilgrims(); fetchFamilies(); fetchClients(); }, [fetchPilgrims, fetchFamilies, fetchClients]);

  const createPilgrim = async () => {
    if (!selectedTrip) { alert('اختر فوجاً أولاً'); return; }
    try {
      const data = { ...formData, trip_id: selectedTrip.id };
      if (!data.family_id) { delete data.family_id; delete data.family_name; }
      if (linkedClientId) data.client_id = linkedClientId;
      await axios.post(`${API}/umrah/pilgrims`, data, { headers: getAuthHeaders() });
      setShowCreate(false);
      setFormData({ first_name: '', last_name: '', passport_number: '', birth_date: '', gender: '', status: 'confirmed', agency_id: '', phone: '', notes: '', has_meals: true, family_id: '', family_name: '', is_family_leader: false, with_bed: true, crew_role: '', discount_category: '' });
      setLinkedClientId(null);
      setClientSearch('');
      fetchPilgrims();
      fetchClients();
      onRefresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'خطأ في تسجيل الحاج');
    }
  };

  // Pick an existing client → autofill the pilgrim form
  const selectClient = (client) => {
    // Split full name into first/last (first word = first name, rest = last name)
    const fullName = (client.name || '').trim();
    const parts = fullName.split(/\s+/);
    const fName = parts[0] || '';
    const lName = parts.slice(1).join(' ') || '';
    setFormData(prev => ({
      ...prev,
      first_name: fName,
      last_name: lName,
      passport_number: client.cin_passport || prev.passport_number,
      phone: client.phone || prev.phone,
      agency_id: client.agency_id || prev.agency_id
    }));
    setLinkedClientId(client.id);
    setClientSearch(client.name);
    setShowClientList(false);
  };

  const clearClientLink = () => {
    setLinkedClientId(null);
    setClientSearch('');
  };

  // Filter clients by search query
  const filteredClients = !clientSearch ? clients : clients.filter(c =>
    (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()) ||
    (c.phone || '').includes(clientSearch) ||
    (c.cin_passport || '').toLowerCase().includes(clientSearch.toLowerCase())
  );

  const deletePilgrim = async (id) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الحاج؟')) return;
    try {
      await axios.delete(`${API}/umrah/pilgrims/${id}`, { headers: getAuthHeaders() });
      fetchPilgrims();
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  const togglePilgrimStatus = async (pilgrim) => {
    const newStatus = pilgrim.status === 'confirmed' ? 'not_confirmed' : 'confirmed';
    try {
      await axios.put(`${API}/umrah/pilgrims/${pilgrim.id}`, { status: newStatus }, { headers: getAuthHeaders() });
      fetchPilgrims();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في تغيير الحالة'); }
  };

  const updatePilgrimData = async (pilgrimId, data) => {
    try {
      await axios.put(`${API}/umrah/pilgrims/${pilgrimId}`, data, { headers: getAuthHeaders() });
      setShowEdit(false);
      setEditPilgrim(null);
      fetchPilgrims();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في التعديل'); }
  };

  const transferPilgrimFn = async (pilgrimId, targetTripId) => {
    try {
      await axios.post(`${API}/umrah/pilgrims/${pilgrimId}/transfer`, 
        { target_trip_id: targetTripId }, { headers: getAuthHeaders() });
      setShowTransfer(false);
      fetchPilgrims();
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في النقل'); }
  };

  const printTable = () => {
    if (!selectedTrip) { alert('اختر فوجاً أولاً'); return; }
    const printWindow = window.open('', '_blank');
    const rows = pilgrims.map((p, i) => `
      <tr>
        <td>${i + 1}</td>
        <td>${p.first_name} ${p.last_name}</td>
        <td style="direction:ltr">${p.passport_number}</td>
        <td>${p.gender === 'male' ? 'ذكر' : 'أنثى'}</td>
        <td>${p.category}</td>
        <td>${p.status === 'confirmed' ? 'مؤكد' : 'غير مؤكد'}</td>
        <td>${p.has_meals !== false ? 'بالإعاشة' : 'بدون'}</td>
        <td>${p.family_name || '-'}</td>
        <td>${p.room_id ? 'مسكّن' : 'غير مسكّن'}</td>
      </tr>
    `).join('');

    const confirmed = pilgrims.filter(p => p.status === 'confirmed').length;
    const withMeals = pilgrims.filter(p => p.has_meals !== false).length;
    const males = pilgrims.filter(p => p.gender === 'male').length;

    printWindow.document.write(`
      <html dir="rtl">
      <head>
        <title>قائمة حجاج - ${selectedTrip.name}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');
          * { font-family: 'Tajawal', 'Segoe UI', Tahoma, Arial, sans-serif; }
          body { padding: 20px; }
          h2 { text-align: center; margin-bottom: 5px; }
          .info { text-align: center; color: #666; margin-bottom: 15px; font-size: 14px; }
          .stats { display: flex; justify-content: center; gap: 30px; margin-bottom: 15px; font-size: 13px; }
          .stats span { background: #f3f4f6; padding: 4px 12px; border-radius: 6px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #1e293b; color: white; padding: 8px 6px; text-align: right; }
          td { border: 1px solid #ddd; padding: 6px; text-align: right; }
          tr:nth-child(even) { background: #f9fafb; }
          .footer { text-align: center; margin-top: 15px; font-size: 11px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h2>قائمة حجاج - ${selectedTrip.name}</h2>
        <div class="info">تاريخ المغادرة: ${selectedTrip.departure_date || '-'} | تاريخ الطباعة: ${new Date().toLocaleDateString('ar-DZ')}</div>
        <div class="stats">
          <span>الإجمالي: ${pilgrims.length}</span>
          <span>مؤكد: ${confirmed}</span>
          <span>ذكور: ${males} | إناث: ${pilgrims.length - males}</span>
          <span>بالإعاشة: ${withMeals} | بدون: ${pilgrims.length - withMeals}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>الاسم الكامل</th><th>رقم الجواز</th><th>الجنس</th>
              <th>الفئة</th><th>الحالة</th><th>الإعاشة</th><th>العائلة</th><th>التسكين</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">وكالات صنهاجة للسفر</div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Trip Selector */}
      <div className="flex items-center gap-4">
        <Select value={selectedTrip?.id || ''} onValueChange={v => setSelectedTrip(trips.find(t => t.id === v))}>
          <SelectTrigger className="w-64"><SelectValue placeholder="اختر الفوج" /></SelectTrigger>
          <SelectContent>
            {trips.filter(t => t && t.id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreate(true)} disabled={!selectedTrip} data-testid="add-pilgrim-btn" title={!selectedTrip ? 'اختر الفوج أولاً من القائمة' : ''}>
          <UserPlus className="h-4 w-4 ml-2" /> تسجيل حاج
        </Button>
        <Button variant="outline" onClick={() => setShowFamily(true)} disabled={!selectedTrip}>
          <Users className="h-4 w-4 ml-2" /> إنشاء عائلة
        </Button>
        <Button variant="outline" onClick={printTable} className="bg-brand-50 hover:bg-brand-100" data-testid="print-table-btn">
          <Printer className="h-4 w-4 ml-2" /> طباعة
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            if (!selectedTrip) return;
            openServerPdf(`${API}/umrah/export/pilgrims/${selectedTrip.id}`, { format: 'pdf' });
          }}
          disabled={!selectedTrip}
          className="bg-red-50 hover:bg-red-100 text-red-700 border-red-300"
          data-testid="download-pdf-btn"
        >
          <Printer className="h-4 w-4 ml-2" /> تحميل PDF
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Input placeholder="بحث..." className="w-48" value={filters.search} 
               onChange={e => setFilters({...filters, search: e.target.value})} />
        <Select value={filters.gender} onValueChange={v => setFilters({...filters, gender: v === 'all' ? '' : v})}>
          <SelectTrigger className="w-32"><SelectValue placeholder="الجنس" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="male">ذكر</SelectItem>
            <SelectItem value="female">أنثى</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.category} onValueChange={v => setFilters({...filters, category: v === 'all' ? '' : v})}>
          <SelectTrigger className="w-32"><SelectValue placeholder="الفئة" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="ADT">بالغ</SelectItem>
            <SelectItem value="CHD">طفل</SelectItem>
            <SelectItem value="INF">رضيع</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.agency_id} onValueChange={v => setFilters({...filters, agency_id: v === 'all' ? '' : v})}>
          <SelectTrigger className="w-40"><SelectValue placeholder="الوكالة" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            {agencies.filter(a => a && a.id).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filters.status} onValueChange={v => setFilters({...filters, status: v === 'all' ? '' : v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="الحالة" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="confirmed">مؤكد</SelectItem>
            <SelectItem value="not_confirmed">غير مؤكد</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filters.has_meals} onValueChange={v => setFilters({...filters, has_meals: v === 'all' ? '' : v})}>
          <SelectTrigger className="w-36"><SelectValue placeholder="الإعاشة" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">الكل</SelectItem>
            <SelectItem value="true">بالإعاشة</SelectItem>
            <SelectItem value="false">بدون إعاشة</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Pilgrims Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">الاسم</TableHead>
                <TableHead className="text-right">الجواز</TableHead>
                <TableHead className="text-right">الجنس</TableHead>
                <TableHead className="text-right">الفئة</TableHead>
                <TableHead className="text-right">السرير</TableHead>
                <TableHead className="text-right">الحالة</TableHead>
                <TableHead className="text-right">الإعاشة</TableHead>
                <TableHead className="text-right">العائلة</TableHead>
                <TableHead className="text-right">الغرفة</TableHead>
                <TableHead className="text-right">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pilgrims.map(p => (
                <TableRow key={p.id} className={p.crew_role ? 'bg-amber-50/40' : ''}>
                  <TableCell className="font-medium">
                    {p.first_name} {p.last_name}
                    {p.crew_role && (
                      <Badge className="ml-2 bg-amber-100 text-amber-800 border border-amber-300" data-testid={`crew-badge-${p.id}`}>
                        🎁 {p.crew_role}
                      </Badge>
                    )}
                    {p.discount_category && !p.crew_role && (
                      <Badge className="ml-2 bg-emerald-100 text-emerald-800 border border-emerald-300" data-testid={`discount-badge-${p.id}`}>
                        🎖️ {p.discount_category}
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{p.passport_number}</TableCell>
                  <TableCell>
                    <Badge variant={p.gender === 'male' ? 'default' : 'secondary'}>
                      {p.gender === 'male' ? 'ذكر' : 'أنثى'}
                    </Badge>
                  </TableCell>
                  <TableCell><Badge variant="outline">{p.category}</Badge></TableCell>
                  <TableCell>
                    {/* 🆕 شارة حالة السرير */}
                    {(() => {
                      const cat = p.category || 'ADT';
                      const effective = cat === 'ADT' ? true : cat === 'INF' ? false : (p.with_bed !== false);
                      return (
                        <Badge variant="outline" className={effective ? 'bg-yellow-50 text-yellow-800 border-yellow-300' : 'bg-gray-50 text-gray-600'} data-testid={`bed-badge-${p.id}`}>
                          {effective ? '🛏️ بسرير' : 'بدون سرير'}
                        </Badge>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={p.status === 'confirmed' ? 'default' : 'destructive'}>
                      {p.status === 'confirmed' ? 'مؤكد' : 'غير مؤكد'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={p.has_meals !== false ? 'bg-green-50 text-green-700' : 'bg-gray-50 text-gray-500'}>
                      {p.has_meals !== false ? 'بالإعاشة' : 'بدون'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {p.family_name ? (
                      <Badge variant="outline" className="bg-purple-50 text-purple-700">
                        {p.family_name} {p.is_family_leader && '(رب)'}
                      </Badge>
                    ) : '-'}
                  </TableCell>
                  <TableCell>{p.room_id ? <Badge variant="outline" className="bg-green-50">مسكّن</Badge> : <Badge variant="outline" className="bg-red-50 text-red-600">غير مسكّن</Badge>}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" title="تعديل الحالة" onClick={() => togglePilgrimStatus(p)}>
                        {p.status === 'confirmed' 
                          ? <Check className="h-3 w-3 text-green-600" />
                          : <X className="h-3 w-3 text-red-600" />
                        }
                      </Button>
                      <Button size="sm" variant="ghost" title="تعديل" onClick={() => { setEditPilgrim(p); setShowEdit(true); }}>
                        <Edit className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" title="نقل" onClick={() => { setTransferPilgrim(p); setShowTransfer(true); }}>
                        <ArrowRightLeft className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        title="🧾 طباعة عقد عمرة (PDF)"
                        className="text-brand-600 hover:bg-brand-100"
                        data-testid={`print-umrah-contract-${p.id}`}
                        onClick={() => {
                          const t = localStorage.getItem('token');
                          window.open(`${API}/umrah/pilgrims/${p.id}/contract?token=${encodeURIComponent(t)}`, '_blank');
                        }}
                      >
                        <Printer className="h-3 w-3" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-600" title="حذف" onClick={() => deletePilgrim(p.id)}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {pilgrims.length === 0 && (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-gray-500">
                    {selectedTrip ? 'لا يوجد حجاج مسجلون' : 'اختر فوجاً لعرض الحجاج'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <div className="text-sm text-gray-500">إجمالي: {pilgrims.length} حاج</div>

      {/* Create Pilgrim Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>تسجيل حاج جديد</DialogTitle>
          </DialogHeader>
          <ErrorBoundary>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto">
            {/* === Existing Client Lookup === */}
            <div className="bg-brand-50 border border-brand-200 rounded p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-brand-800 font-semibold flex items-center gap-1">
                  👤 ربط بعميل موجود (لتجنب التكرار)
                </Label>
                {linkedClientId && (
                  <Button type="button" size="sm" variant="ghost" onClick={clearClientLink} className="h-7 text-xs text-red-600">
                    إلغاء الربط ✕
                  </Button>
                )}
              </div>
              {linkedClientId ? (
                <div className="bg-green-100 border border-green-300 rounded p-2 text-sm flex items-center gap-2">
                  <Check className="h-4 w-4 text-green-700" />
                  <span className="text-green-800 font-medium">مربوط بالعميل: {clientSearch}</span>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    placeholder="ابحث بالاسم، الهاتف، أو رقم الهوية..."
                    value={clientSearch}
                    onChange={e => { setClientSearch(e.target.value); setShowClientList(true); }}
                    onFocus={() => setShowClientList(true)}
                    data-testid="client-search-input"
                  />
                  {showClientList && clientSearch && filteredClients.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
                      {filteredClients.slice(0, 10).map(c => (
                        <div
                          key={c.id}
                          onClick={() => selectClient(c)}
                          className="p-2 hover:bg-brand-50 cursor-pointer border-b last:border-b-0 text-sm"
                          data-testid="client-search-item"
                        >
                          <div className="font-medium">{c.name}</div>
                          <div className="text-xs text-gray-500 flex gap-3">
                            {c.phone && <span>📞 {c.phone}</span>}
                            {c.cin_passport && <span style={{direction:'ltr'}}>🆔 {c.cin_passport}</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {showClientList && clientSearch && filteredClients.length === 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-amber-300 rounded shadow-lg p-2 text-sm text-amber-700">
                      لا يوجد عميل بهذا البحث — املأ البيانات يدوياً وسيتم إنشاء عميل جديد تلقائياً
                    </div>
                  )}
                </div>
              )}
              <p className="text-xs text-brand-700">
                💡 إذا لم تربط بعميل، سيتم إنشاء عميل جديد تلقائياً عند الحفظ
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الاسم</Label>
                <Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
              </div>
              <div>
                <Label>اللقب</Label>
                <Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
              </div>
            </div>
            <div>
              <Label>رقم جواز السفر</Label>
              <Input value={formData.passport_number} onChange={e => setFormData({...formData, passport_number: e.target.value})} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>تاريخ الميلاد</Label>
                <Input type="date" value={formData.birth_date} onChange={e => setFormData({...formData, birth_date: e.target.value})} />
              </div>
              <div>
                <Label>الجنس</Label>
                <Select value={formData.gender || undefined} onValueChange={v => setFormData({...formData, gender: v})}>
                  <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">ذكر</SelectItem>
                    <SelectItem value="female">أنثى</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>الوكالة</Label>
                <Select value={formData.agency_id || undefined} onValueChange={v => setFormData({...formData, agency_id: v})}>
                  <SelectTrigger><SelectValue placeholder="اختر" /></SelectTrigger>
                  <SelectContent>
                    {agencies.filter(a => a && a.id).map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>الحالة</Label>
                <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="confirmed">مؤكد</SelectItem>
                    <SelectItem value="not_confirmed">غير مؤكد</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>الهاتف (اختياري)</Label>
              <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={formData.has_meals} 
                       onChange={e => setFormData({...formData, has_meals: e.target.checked})}
                       className="w-4 h-4" />
                <span className="font-medium">بالإعاشة</span>
              </label>
              <span className="text-sm text-gray-500">
                {formData.has_meals ? 'المعتمر يشمل الإعاشة' : 'المعتمر بدون إعاشة'}
              </span>
            </div>

            {/* 🆕 خاصية السرير — تظهر حسب فئة العمر */}
            {(() => {
              const cat = formData.birth_date ? computeCategory(formData.birth_date) : null;
              if (!cat) return null;
              const catLabel = cat === 'ADT' ? '👨 بالغ' : cat === 'CHD' ? '🧒 طفل' : '👶 رضيع';
              const locked = cat !== 'CHD';
              const effective = cat === 'ADT' ? true : cat === 'INF' ? false : !!formData.with_bed;
              return (
                <div className={`p-3 rounded-lg border ${locked ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-200'}`} data-testid="bed-status-block">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">حالة السرير</span>
                    <Badge variant="outline" className="text-xs">{catLabel}</Badge>
                  </div>
                  <label className={`flex items-center gap-2 ${locked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
                    <input
                      type="checkbox"
                      data-testid="with-bed-checkbox"
                      checked={effective}
                      disabled={locked}
                      onChange={e => setFormData({ ...formData, with_bed: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <span className="font-medium">{effective ? 'بسرير 🛏️ (يحتسب من سعة الغرفة)' : 'بدون سرير (لا يحتسب)'}</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1">
                    {cat === 'ADT' && 'البالغ يأخذ سريرًا دائمًا — يحتسب من السعة.'}
                    {cat === 'INF' && 'الرضيع لا يأخذ سريرًا — لا يحتسب من السعة.'}
                    {cat === 'CHD' && 'الطفل بدون سرير لا يحتسب من سعة الغرفة (يبقى مع والديه).'}
                  </p>
                </div>
              );
            })()}

            {/* 🆕 دور الطاقم — يحوّل المعتمر إلى طاقم مجاني في الرحلة الجوية */}
            <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="crew-role-block">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm">🎁 دور الطاقم (اختياري)</span>
                <Badge variant="outline" className="text-xs">للرحلة الجوية</Badge>
              </div>
              <select
                value={formData.crew_role || ''}
                onChange={e => setFormData({ ...formData, crew_role: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-white"
                data-testid="crew-role-select"
              >
                <option value="">— ليس من الطاقم (راكب عادي) —</option>
                <option value="مرشد">مرشد</option>
                <option value="طبيب">طبيب</option>
                <option value="مرافق">مرافق</option>
                <option value="إداري">إداري</option>
                <option value="مدير">مدير</option>
              </select>
              {formData.crew_role && (
                <p className="text-xs text-amber-700 mt-1">
                  ⚠️ سيظهر هذا المعتمر كـ «🎁 طاقم مجاني» في كشف الرحلة الجوية — لن تُحسب له تكلفة طيران.
                </p>
              )}
              {formData.crew_role && (
                <div className="text-xs text-amber-900 mt-2 bg-amber-100 border border-amber-300 rounded p-2">
                  ℹ️ <strong>طريقة عمل الطاقم في النظام:</strong>
                  <ul className="list-disc pr-4 mt-1 space-y-0.5">
                    <li>يُسجَّل العضو كعميل في CRM وكمعتمر في الفوج</li>
                    <li><strong>لا تُنشأ له رحلة منظمة</strong> (لا بيع، لا دفعات منه)</li>
                    <li>لا يُحتسب من حصة الوكالة المدفوعة من المقاعد</li>
                    <li>تكلفته على الوكالة = <code>price_crew_free</code> المحدَّد على الرحلة الجوية (تذهب لشركة الطيران)</li>
                  </ul>
                </div>
              )}
            </div>

            {/* 🆕 فئة المستفيد من تخفيض الطيران */}
            <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200" data-testid="discount-category-block">
              <div className="flex items-center justify-between mb-2">
                <span className="font-semibold text-sm">🎖️ مستفيد من تخفيض الطيران (اختياري)</span>
                <Badge variant="outline" className="text-xs">سعر مخفّض</Badge>
              </div>
              <select
                value={formData.discount_category || ''}
                onChange={e => setFormData({ ...formData, discount_category: e.target.value })}
                className="w-full px-3 py-2 border rounded-md bg-white"
                data-testid="discount-category-select"
                disabled={!!formData.crew_role}
              >
                <option value="">— بدون تخفيض —</option>
                <option value="مجاهد">مجاهد</option>
                <option value="ابن شهيد">ابن شهيد</option>
                <option value="أخرى">أخرى</option>
              </select>
              {formData.discount_category && (
                <p className="text-xs text-emerald-700 mt-1">
                  ✅ سيتم احتساب تكلفة الطيران بالسعر المخفّض المسجَّل على الرحلة الجوية.
                </p>
              )}
              {formData.crew_role && (
                <p className="text-xs text-gray-500 mt-1">
                  ℹ️ معطّل لأن المعتمر مسجّل كطاقم (الطاقم مجاني تلقائياً).
                </p>
              )}
            </div>

            <div className="border-t pt-3">
              <Label className="font-semibold">معلومات العائلة (اختياري)</Label>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Label className="text-xs">اسم العائلة</Label>
                  <Input value={formData.family_name} onChange={e => setFormData({...formData, family_name: e.target.value})} placeholder="مثلاً: عائلة بن علي" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_family_leader} 
                           onChange={e => setFormData({...formData, is_family_leader: e.target.checked})} />
                    <span className="text-sm">رب العائلة</span>
                  </label>
                </div>
              </div>
              {families.length > 0 && (
                <div className="mt-2">
                  <Label className="text-xs">أو انضم لعائلة موجودة</Label>
                  <Select value={formData.family_id || undefined} onValueChange={v => {
                    const fam = families.find(f => f.family_id === v);
                    setFormData({...formData, family_id: v, family_name: fam?.family_name || ''});
                  }}>
                    <SelectTrigger><SelectValue placeholder="اختر عائلة" /></SelectTrigger>
                    <SelectContent>
                      {families.filter(f => f && f.family_id).map(f => <SelectItem key={f.family_id} value={f.family_id}>{f.family_name} ({f.count} أفراد)</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <Button onClick={createPilgrim} className="w-full" data-testid="submit-pilgrim">تسجيل الحاج</Button>
          </div>
          </ErrorBoundary>
        </DialogContent>
      </Dialog>

      {/* Create Family Dialog */}
      <CreateFamilyDialog open={showFamily} onClose={() => setShowFamily(false)} 
                          tripId={selectedTrip?.id} pilgrims={pilgrims} onRefresh={() => { fetchPilgrims(); fetchFamilies(); }} />

      {/* Transfer Dialog */}
      <Dialog open={showTransfer} onOpenChange={setShowTransfer}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>نقل حاج إلى فوج آخر</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">نقل: {transferPilgrim?.first_name} {transferPilgrim?.last_name}</p>
            <Select onValueChange={v => transferPilgrimFn(transferPilgrim.id, v)}>
              <SelectTrigger><SelectValue placeholder="اختر الفوج المستهدف" /></SelectTrigger>
              <SelectContent>
                {trips.filter(t => t.id !== selectedTrip?.id && t.status === 'open').map(t => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Pilgrim Dialog */}
      {editPilgrim && (
        <EditPilgrimDialog open={showEdit} onClose={() => { setShowEdit(false); setEditPilgrim(null); }}
                           pilgrim={editPilgrim} agencies={agencies} families={families}
                           onSave={updatePilgrimData} />
      )}
    </div>
  );
};
const CreateFamilyDialog = ({ open, onClose, tripId, pilgrims, onRefresh }) => {
  const [familyName, setFamilyName] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);

  const togglePilgrim = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const createFamily = async () => {
    if (!familyName || selectedIds.length < 2) {
      alert('أدخل اسم العائلة واختر عضوين على الأقل');
      return;
    }
    try {
      await axios.post(`${API}/umrah/families`, {
        trip_id: tripId,
        family_name: familyName,
        pilgrim_ids: selectedIds
      }, { headers: getAuthHeaders() });
      setFamilyName('');
      setSelectedIds([]);
      onClose();
      onRefresh();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  const unassignedPilgrims = pilgrims.filter(p => !p.family_id);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>إنشاء مجموعة عائلية</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>اسم العائلة</Label>
            <Input value={familyName} onChange={e => setFamilyName(e.target.value)} placeholder="مثلاً: عائلة محمدي" />
          </div>
          <div>
            <Label>اختر أفراد العائلة ({selectedIds.length} محدد)</Label>
            <div className="max-h-48 overflow-y-auto border rounded-md p-2 mt-1 space-y-1">
              {unassignedPilgrims.map(p => (
                <label key={p.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${selectedIds.includes(p.id) ? 'bg-brand-50' : ''}`}>
                  <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => togglePilgrim(p.id)} />
                  <span className="text-sm">{p.first_name} {p.last_name}</span>
                  <Badge variant="outline" className="text-xs mr-auto">{p.gender === 'male' ? 'ذ' : 'أ'}</Badge>
                </label>
              ))}
              {unassignedPilgrims.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-2">جميع الحجاج منتمون لعائلات</p>
              )}
            </div>
          </div>
          <Button onClick={createFamily} className="w-full" disabled={selectedIds.length < 2}>
            إنشاء العائلة ({selectedIds.length} أفراد)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ EDIT PILGRIM DIALOG ============
const EditPilgrimDialog = ({ open, onClose, pilgrim, agencies, families, onSave }) => {
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (pilgrim) {
      setFormData({
        first_name: pilgrim.first_name || '',
        last_name: pilgrim.last_name || '',
        passport_number: pilgrim.passport_number || '',
        birth_date: pilgrim.birth_date || '',
        gender: pilgrim.gender || '',
        status: pilgrim.status || 'confirmed',
        has_meals: pilgrim.has_meals !== false,
        phone: pilgrim.phone || '',
        notes: pilgrim.notes || '',
        family_id: pilgrim.family_id || '',
        family_name: pilgrim.family_name || '',
        is_family_leader: pilgrim.is_family_leader || false,
        with_bed: pilgrim.with_bed !== false, // 🆕 default true if unset
        crew_role: pilgrim.crew_role || '', // 🆕 دور الطاقم
        discount_category: pilgrim.discount_category || '', // 🆕 فئة المستفيد من التخفيض
      });
    }
  }, [pilgrim]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>تعديل بيانات الحاج</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 max-h-[70vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الاسم</Label>
              <Input value={formData.first_name} onChange={e => setFormData({...formData, first_name: e.target.value})} />
            </div>
            <div>
              <Label>اللقب</Label>
              <Input value={formData.last_name} onChange={e => setFormData({...formData, last_name: e.target.value})} />
            </div>
          </div>
          <div>
            <Label>رقم جواز السفر</Label>
            <Input value={formData.passport_number} onChange={e => setFormData({...formData, passport_number: e.target.value})} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>تاريخ الميلاد</Label>
              <Input type="date" value={formData.birth_date} onChange={e => setFormData({...formData, birth_date: e.target.value})} />
            </div>
            <div>
              <Label>الجنس</Label>
              <Select value={formData.gender} onValueChange={v => setFormData({...formData, gender: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="male">ذكر</SelectItem>
                  <SelectItem value="female">أنثى</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>الحالة</Label>
              <Select value={formData.status} onValueChange={v => setFormData({...formData, status: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="confirmed">مؤكد</SelectItem>
                  <SelectItem value="not_confirmed">غير مؤكد</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>الهاتف</Label>
              <Input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} />
            </div>
          </div>
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={formData.has_meals} 
                     onChange={e => setFormData({...formData, has_meals: e.target.checked})}
                     className="w-4 h-4" />
              <span className="font-medium">بالإعاشة</span>
            </label>
            <span className="text-sm text-gray-500">
              {formData.has_meals ? 'المعتمر يشمل الإعاشة' : 'المعتمر بدون إعاشة'}
            </span>
          </div>

          {/* 🆕 خاصية السرير (قابل للتعديل لاحقًا للأطفال) */}
          {(() => {
            const cat = formData.birth_date ? computeCategory(formData.birth_date) : null;
            if (!cat) return null;
            const catLabel = cat === 'ADT' ? '👨 بالغ' : cat === 'CHD' ? '🧒 طفل' : '👶 رضيع';
            const locked = cat !== 'CHD';
            const effective = cat === 'ADT' ? true : cat === 'INF' ? false : !!formData.with_bed;
            return (
              <div className={`p-3 rounded-lg border ${locked ? 'bg-gray-50 border-gray-200' : 'bg-yellow-50 border-yellow-200'}`} data-testid="edit-bed-status-block">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-sm">حالة السرير</span>
                  <Badge variant="outline" className="text-xs">{catLabel}</Badge>
                </div>
                <label className={`flex items-center gap-2 ${locked ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <input
                    type="checkbox"
                    data-testid="edit-with-bed-checkbox"
                    checked={effective}
                    disabled={locked}
                    onChange={e => setFormData({ ...formData, with_bed: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <span className="font-medium">{effective ? 'بسرير 🛏️ (يحتسب من سعة الغرفة)' : 'بدون سرير (لا يحتسب)'}</span>
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  {cat === 'ADT' && 'البالغ يأخذ سريرًا دائمًا.'}
                  {cat === 'INF' && 'الرضيع لا يأخذ سريرًا.'}
                  {cat === 'CHD' && 'يمكن تعديل حالة السرير للطفل في أي وقت.'}
                </p>
              </div>
            );
          })()}

          {/* 🆕 دور الطاقم — قابل للتعديل */}
          <div className="p-3 rounded-lg border bg-amber-50 border-amber-200" data-testid="edit-crew-role-block">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm">🎁 دور الطاقم (اختياري)</span>
              <Badge variant="outline" className="text-xs">للرحلة الجوية</Badge>
            </div>
            <select
              value={formData.crew_role || ''}
              onChange={e => setFormData({ ...formData, crew_role: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-white"
              data-testid="edit-crew-role-select"
            >
              <option value="">— ليس من الطاقم —</option>
              <option value="مرشد">مرشد</option>
              <option value="طبيب">طبيب</option>
              <option value="مرافق">مرافق</option>
              <option value="إداري">إداري</option>
              <option value="مدير">مدير</option>
            </select>
            {formData.crew_role && (
              <p className="text-xs text-amber-700 mt-1">
                ⚠️ سيظهر هذا المعتمر كـ «🎁 طاقم مجاني» في كشف الرحلة الجوية.
              </p>
            )}
          </div>

          {/* 🆕 فئة المستفيد من تخفيض الطيران — قابل للتعديل */}
          <div className="p-3 rounded-lg border bg-emerald-50 border-emerald-200" data-testid="edit-discount-category-block">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold text-sm">🎖️ مستفيد من تخفيض الطيران (اختياري)</span>
              <Badge variant="outline" className="text-xs">سعر مخفّض</Badge>
            </div>
            <select
              value={formData.discount_category || ''}
              onChange={e => setFormData({ ...formData, discount_category: e.target.value })}
              className="w-full px-3 py-2 border rounded-md bg-white"
              data-testid="edit-discount-category-select"
              disabled={!!formData.crew_role}
            >
              <option value="">— بدون تخفيض —</option>
              <option value="مجاهد">مجاهد</option>
              <option value="ابن شهيد">ابن شهيد</option>
              <option value="أخرى">أخرى</option>
            </select>
            {formData.discount_category && (
              <p className="text-xs text-emerald-700 mt-1">
                ✅ سيتم احتساب تكلفة الطيران بالسعر المخفّض.
              </p>
            )}
            {formData.crew_role && (
              <p className="text-xs text-gray-500 mt-1">
                ℹ️ معطّل لأن المعتمر مسجّل كطاقم.
              </p>
            )}
          </div>

          <div className="border-t pt-3">
            <Label className="font-semibold">معلومات العائلة</Label>
            <div className="grid grid-cols-2 gap-3 mt-2">
              <div>
                <Label className="text-xs">اسم العائلة</Label>
                <Input value={formData.family_name} onChange={e => setFormData({...formData, family_name: e.target.value})} />
              </div>
              <div className="flex items-end">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={formData.is_family_leader} 
                         onChange={e => setFormData({...formData, is_family_leader: e.target.checked})} />
                  <span className="text-sm">رب العائلة</span>
                </label>
              </div>
            </div>
            {families?.length > 0 && (
              <div className="mt-2">
                <Label className="text-xs">انضم لعائلة</Label>
                <Select value={formData.family_id || 'none'} onValueChange={v => {
                  if (v === 'none') {
                    setFormData({...formData, family_id: '', family_name: ''});
                  } else {
                    const fam = families.find(f => f.family_id === v);
                    setFormData({...formData, family_id: v, family_name: fam?.family_name || ''});
                  }
                }}>
                  <SelectTrigger><SelectValue placeholder="بدون عائلة" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">بدون عائلة</SelectItem>
                    {families.map(f => <SelectItem key={f.family_id} value={f.family_id}>{f.family_name} ({f.count} أفراد)</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={() => onSave(pilgrim.id, formData)} className="flex-1" data-testid="save-edit-pilgrim">
              حفظ التعديلات
            </Button>
            <Button variant="outline" onClick={onClose}>إلغاء</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ ROOMS TAB ============
const RoomsTab = ({ trips, selectedTrip, setSelectedTrip }) => {
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  const [rooms, setRooms] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [unassignedPilgrims, setUnassignedPilgrims] = useState([]);
  const [showCreateRoom, setShowCreateRoom] = useState(false);
  const [showBulkCreate, setShowBulkCreate] = useState(false);
  const [roomForm, setRoomForm] = useState({ room_number: '', room_type: 'double', capacity: '' });
  const [bulkConfig, setBulkConfig] = useState({ single: 0, double: 0, triple: 0, quad: 0, group: 0 });
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [showAssign, setShowAssign] = useState(false);

  const fetchRooms = useCallback(async () => {
    if (!selectedTrip) return;
    try {
      const res = await axios.get(`${API}/umrah/rooms?trip_id=${selectedTrip.id}`, { headers: getAuthHeaders() });
      setRooms(res.data);
    } catch (e) { console.error(e); }
  }, [selectedTrip]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedTrip) return;
    try {
      const res = await axios.get(`${API}/umrah/analytics/${selectedTrip.id}`, { headers: getAuthHeaders() });
      setAnalytics(res.data);
    } catch (e) { console.error(e); }
  }, [selectedTrip]);

  const fetchUnassigned = useCallback(async () => {
    if (!selectedTrip) return;
    try {
      const res = await axios.get(`${API}/umrah/pilgrims?trip_id=${selectedTrip.id}`, { headers: getAuthHeaders() });
      setUnassignedPilgrims(res.data.filter(p => !p.room_id));
    } catch (e) { console.error(e); }
  }, [selectedTrip]);

  useEffect(() => { fetchRooms(); fetchAnalytics(); fetchUnassigned(); }, [fetchRooms, fetchAnalytics, fetchUnassigned]);

  const createRoom = async () => {
    try {
      const data = { ...roomForm, trip_id: selectedTrip.id };
      if (data.capacity) data.capacity = parseInt(data.capacity);
      else delete data.capacity;
      await axios.post(`${API}/umrah/rooms`, data, { headers: getAuthHeaders() });
      setShowCreateRoom(false);
      setRoomForm({ room_number: '', room_type: 'double', capacity: '' });
      fetchRooms();
      fetchAnalytics();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  const bulkCreateRooms = async () => {
    try {
      await axios.post(`${API}/umrah/rooms/bulk-create?trip_id=${selectedTrip.id}`, bulkConfig, { headers: getAuthHeaders() });
      setShowBulkCreate(false);
      fetchRooms();
      fetchAnalytics();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  const autoAllocate = async () => {
    if (!window.confirm('هل تريد تشغيل التسكين التلقائي الذكي؟')) return;
    try {
      const res = await axios.post(`${API}/umrah/rooms/auto-allocate/${selectedTrip.id}`, {}, { headers: getAuthHeaders() });
      alert(`${res.data.message}\n${res.data.warnings?.length > 0 ? '\nتحذيرات:\n' + res.data.warnings.join('\n') : ''}`);
      fetchRooms();
      fetchAnalytics();
      fetchUnassigned();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في التسكين التلقائي'); }
  };

  const assignToRoom = async (roomId, pilgrimIds) => {
    try {
      await axios.post(`${API}/umrah/rooms/assign`, { room_id: roomId, pilgrim_ids: pilgrimIds }, { headers: getAuthHeaders() });
      fetchRooms();
      fetchAnalytics();
      fetchUnassigned();
      setShowAssign(false);
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  const unassignPilgrim = async (pilgrimId) => {
    try {
      await axios.post(`${API}/umrah/rooms/unassign/${pilgrimId}`, {}, { headers: getAuthHeaders() });
      fetchRooms();
      fetchAnalytics();
      fetchUnassigned();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ'); }
  };

  // ===== Drag & Drop room assignment =====
  const movePilgrim = async (pilgrimId, targetRoomId) => {
    try {
      await axios.post(
        `${API}/umrah/rooms/move-pilgrim`,
        { pilgrim_id: pilgrimId, target_room_id: targetRoomId },
        { headers: getAuthHeaders() }
      );
      fetchRooms();
      fetchAnalytics();
      fetchUnassigned();
    } catch (e) { alert(e.response?.data?.detail || 'خطأ في النقل'); }
  };

  const handleDragStart = (e, pilgrimId, sourceRoomId) => {
    e.dataTransfer.setData('pilgrimId', pilgrimId);
    e.dataTransfer.setData('sourceRoomId', sourceRoomId || '');
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; };
  const handleDrop = async (e, targetRoom) => {
    e.preventDefault();
    e.currentTarget.classList.remove('ring-2', 'ring-brand-400');
    const pilgrimId = e.dataTransfer.getData('pilgrimId');
    const sourceRoomId = e.dataTransfer.getData('sourceRoomId');
    if (!pilgrimId || !targetRoom) return;
    if (sourceRoomId === targetRoom.id) return;
    if (targetRoom.is_full) { alert('الغرفة ممتلئة'); return; }
    if (sourceRoomId) {
      // Move between rooms
      await movePilgrim(pilgrimId, targetRoom.id);
    } else {
      // Assign from unassigned panel
      await assignToRoom(targetRoom.id, [pilgrimId]);
    }
  };
  const handleDragEnter = (e) => { e.currentTarget.classList.add('ring-2', 'ring-brand-400'); };
  const handleDragLeave = (e) => { e.currentTarget.classList.remove('ring-2', 'ring-brand-400'); };

  const deleteRoom = async (roomId) => {
    if (!window.confirm('حذف هذه الغرفة؟')) return;
    try {
      await axios.delete(`${API}/umrah/rooms/${roomId}`, { headers: getAuthHeaders() });
      fetchRooms();
      fetchAnalytics();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail || 'خطأ';
      // 🆕 Non-empty room → only the super admin sees the «force-delete» path.
      // Pilgrims return to the "في انتظار التسكين" pool (their pilgrim row stays;
      // only the room link is severed) and the action is audit-logged backend-side.
      if (status === 400 && isSuperAdmin) {
        const ok = window.confirm(
          '⚠️ هذه الغرفة تحتوي على حجاج مُسكَّنين.\n\n' +
          'هل تريد المتابعة وحذفها قسراً؟\n' +
          '• سيتم إلغاء تسكين جميع الحجاج وإرجاعهم إلى قائمة «في انتظار التسكين».\n' +
          '• تبقى بياناتهم محفوظة في الفوج.\n' +
          '• سيُسجَّل هذا الإجراء في سجل النشاط للتدقيق.\n\n' +
          'هل أنت متأكد؟'
        );
        if (!ok) return;
        try {
          const r = await axios.delete(`${API}/umrah/rooms/${roomId}?force=true`, { headers: getAuthHeaders() });
          alert(`✅ تم حذف الغرفة وإرجاع ${r.data?.unassigned_pilgrim_count || 0} حاجّ إلى قائمة الانتظار`);
          fetchRooms();
          fetchAnalytics();
          fetchUnassigned();
        } catch (e2) {
          alert(e2.response?.data?.detail || 'فشل حذف الغرفة قسراً');
        }
      } else {
        alert(detail);
      }
    }
  };

  const exportRooms = () => {
    if (!selectedTrip) { alert('اختر فوجاً أولاً'); return; }
    const printWindow = window.open('', '_blank');
    const rows = rooms.map((room, i) => {
      const names = room.pilgrims?.map(p => `${p.first_name} ${p.last_name}`).join('، ') || '-';
      const genderLabel = room.gender_type === 'male' ? 'ذكور' : room.gender_type === 'female' ? 'إناث' : room.gender_type === 'mixed' ? 'مختلط' : '-';
      return `<tr>
        <td>${i + 1}</td>
        <td>${room.room_number}</td>
        <td>${room.room_type}</td>
        <td>${room.capacity}</td>
        <td>${room.current_occupancy}/${room.capacity}</td>
        <td>${genderLabel}</td>
        <td>${room.has_family ? 'نعم' : 'لا'}</td>
        <td>${names}</td>
      </tr>`;
    }).join('');

    const fullRooms = rooms.filter(r => r.is_full).length;
    const emptyBeds = rooms.reduce((sum, r) => sum + (r.capacity - r.current_occupancy), 0);

    printWindow.document.write(`
      <html dir="rtl">
      <head>
        <title>تسكين الغرف - ${selectedTrip.name}</title>
        <style>
          @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800&display=swap');
          * { font-family: 'Tajawal', 'Segoe UI', Tahoma, Arial, sans-serif; }
          body { padding: 20px; }
          h2 { text-align: center; margin-bottom: 5px; }
          .info { text-align: center; color: #666; margin-bottom: 15px; font-size: 14px; }
          .stats { display: flex; justify-content: center; gap: 30px; margin-bottom: 15px; font-size: 13px; }
          .stats span { background: #f3f4f6; padding: 4px 12px; border-radius: 6px; }
          table { width: 100%; border-collapse: collapse; font-size: 13px; }
          th { background: #1e293b; color: white; padding: 8px 6px; text-align: right; }
          td { border: 1px solid #ddd; padding: 6px; text-align: right; }
          tr:nth-child(even) { background: #f9fafb; }
          .footer { text-align: center; margin-top: 15px; font-size: 11px; color: #999; }
          @media print { body { padding: 0; } }
        </style>
      </head>
      <body>
        <h2>تسكين الغرف - ${selectedTrip.name}</h2>
        <div class="info">تاريخ الطباعة: ${new Date().toLocaleDateString('ar-DZ')}</div>
        <div class="stats">
          <span>إجمالي الغرف: ${rooms.length}</span>
          <span>ممتلئة: ${fullRooms}</span>
          <span>أسرّة فارغة: ${emptyBeds}</span>
        </div>
        <table>
          <thead>
            <tr>
              <th>#</th><th>رقم الغرفة</th><th>النوع</th><th>السعة</th>
              <th>الإشغال</th><th>الجنس</th><th>عائلة</th><th>الحجاج</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
        <div class="footer">وكالات صنهاجة للسفر</div>
        <script>window.onload = function() { window.print(); }</script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <div className="space-y-4 mt-4">
      {/* Trip Selector & Actions */}
      <div className="flex items-center gap-3 flex-wrap">
        <Select value={selectedTrip?.id || ''} onValueChange={v => setSelectedTrip(trips.find(t => t.id === v))}>
          <SelectTrigger className="w-64"><SelectValue placeholder="اختر الفوج" /></SelectTrigger>
          <SelectContent>
            {trips.filter(t => t && t.id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={() => setShowCreateRoom(true)} disabled={!selectedTrip} size="sm">
          <Plus className="h-4 w-4 ml-1" /> غرفة
        </Button>
        <Button onClick={() => setShowBulkCreate(true)} disabled={!selectedTrip} size="sm" variant="outline">
          <Plus className="h-4 w-4 ml-1" /> إنشاء مجمع
        </Button>
        <Button onClick={autoAllocate} disabled={!selectedTrip} size="sm" variant="default" className="bg-green-600 hover:bg-green-700" data-testid="auto-allocate-btn">
          <RefreshCw className="h-4 w-4 ml-1" /> تسكين تلقائي ذكي
        </Button>
        <Button onClick={exportRooms} disabled={!selectedTrip} size="sm" variant="outline" data-testid="print-rooms-btn">
          <Printer className="h-4 w-4 ml-1" /> طباعة
        </Button>
      </div>

      {/* Analytics */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">إجمالي الغرف</div>
            <div className="text-xl font-bold">{analytics.total_rooms}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">ممتلئة</div>
            <div className="text-xl font-bold text-green-600">{analytics.full_rooms}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">جزئية</div>
            <div className="text-xl font-bold text-yellow-600">{analytics.partial_rooms}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">أسرّة فارغة</div>
            <div className="text-xl font-bold text-red-600">{analytics.empty_beds}</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">نسبة الإشغال</div>
            <div className="text-xl font-bold text-brand-600">{analytics.occupancy_rate}%</div>
          </Card>
          <Card className="p-3 text-center">
            <div className="text-xs text-gray-500">غير مسكّنين</div>
            <div className="text-xl font-bold text-orange-600">{analytics.unassigned_pilgrims}</div>
          </Card>
        </div>
      )}

      {/* Unassigned Pilgrims Panel — Drag from here onto a room */}
      {unassignedPilgrims.length > 0 && (
        <Card className="border-orange-300 bg-orange-50/50" data-testid="unassigned-panel">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-orange-600" />
                <span className="font-semibold text-orange-800 text-sm">حجاج غير مسكّنين ({unassignedPilgrims.length})</span>
              </div>
              <span className="text-xs text-gray-600">💡 اسحب الحاج وأفلته على غرفة</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {unassignedPilgrims.map(p => (
                <div
                  key={p.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, p.id, '')}
                  className="flex items-center gap-2 bg-white border border-orange-200 rounded-md px-2 py-1 text-xs cursor-grab active:cursor-grabbing hover:shadow-md hover:border-orange-400 transition-all"
                  data-testid={`unassigned-pilgrim-${p.id}`}
                  title="اسحب الحاج لإلقائه على غرفة"
                >
                  <span className="text-gray-600">⠿</span>
                  <span className="font-medium">{p.first_name} {p.last_name}</span>
                  {p.gender === 'male' ? <span className="text-brand-500">♂</span> : <span className="text-pink-500">♀</span>}
                  {p.family_name && <span className="text-purple-600">[{p.family_name}]</span>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Rooms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rooms.map(room => (
          <Card
            key={room.id}
            className={`transition-all ${room.is_full ? 'border-green-300 bg-green-50' : room.current_occupancy > 0 ? 'border-yellow-300 bg-yellow-50' : 'border-gray-200'}`}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, room)}
            data-testid={`room-card-${room.id}`}
          >
            <CardContent className="p-4">
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <BedDouble className="h-4 w-4 text-gray-500" />
                  <span className="font-bold">{room.room_number}</span>
                  <Badge variant="outline" className="text-xs">{room.room_type}</Badge>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-sm font-bold">{room.current_occupancy}/{room.capacity}</span>
                  {room.has_family && <Badge className="bg-purple-100 text-purple-700 text-xs">عائلة</Badge>}
                  {room.gender_type === 'mixed' && <Badge className="bg-orange-100 text-orange-700 text-xs">مختلط</Badge>}
                  {room.gender_type === 'male' && <Badge className="bg-brand-100 text-brand-700 text-xs">ذكور</Badge>}
                  {room.gender_type === 'female' && <Badge className="bg-pink-100 text-pink-700 text-xs">إناث</Badge>}
                </div>
              </div>
              
              {/* Occupants */}
              <div className="space-y-1 min-h-[40px]">
                {room.pilgrims?.map(p => (
                  <div
                    key={p.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, p.id, room.id)}
                    className="flex items-center justify-between text-xs bg-white rounded px-2 py-1 cursor-grab active:cursor-grabbing hover:shadow-sm hover:bg-brand-50 transition-colors"
                    title="اسحب لنقل الحاج إلى غرفة أخرى"
                  >
                    <span className="flex items-center gap-1"><span className="text-gray-400">⠿</span>{p.first_name} {p.last_name}</span>
                    <div className="flex items-center gap-1">
                      {p.family_name && <span className="text-purple-600">[{p.family_name}]</span>}
                      <button onClick={() => unassignPilgrim(p.id)} className="text-red-400 hover:text-red-600">✕</button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Actions */}
              <div className="flex gap-1 mt-2 border-t pt-2">
                {!room.is_full && (
                  <Button size="sm" variant="ghost" className="text-xs" onClick={() => { setSelectedRoom(room); setShowAssign(true); }}>
                    <Plus className="h-3 w-3 ml-1" /> إضافة
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-xs text-red-600" onClick={() => deleteRoom(room.id)}>
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {rooms.length === 0 && selectedTrip && (
          <div className="col-span-3 text-center py-8 text-gray-500">لا توجد غرف بعد. أنشئ غرفاً أولاً.</div>
        )}
      </div>

      {/* Create Room Dialog */}
      <Dialog open={showCreateRoom} onOpenChange={setShowCreateRoom}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>إنشاء غرفة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>رقم الغرفة</Label>
              <Input value={roomForm.room_number} onChange={e => setRoomForm({...roomForm, room_number: e.target.value})} />
            </div>
            <div>
              <Label>النوع</Label>
              <Select value={roomForm.room_type} onValueChange={v => setRoomForm({...roomForm, room_type: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="single">فردية (1)</SelectItem>
                  <SelectItem value="double">ثنائية (2)</SelectItem>
                  <SelectItem value="triple">ثلاثية (3)</SelectItem>
                  <SelectItem value="quad">رباعية (4)</SelectItem>
                  <SelectItem value="group">جماعية (مرنة)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>السعة (اختياري - يحدد تلقائياً)</Label>
              <Input type="number" value={roomForm.capacity} onChange={e => setRoomForm({...roomForm, capacity: e.target.value})} />
            </div>
            <Button onClick={createRoom} className="w-full">إنشاء</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={showBulkCreate} onOpenChange={setShowBulkCreate}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>إنشاء غرف مجمع</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {[['single', 'فردية'], ['double', 'ثنائية'], ['triple', 'ثلاثية'], ['quad', 'رباعية'], ['group', 'جماعية']].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <Label>{label}</Label>
                <Input type="number" className="w-20" min="0" value={bulkConfig[key]} 
                       onChange={e => setBulkConfig({...bulkConfig, [key]: parseInt(e.target.value) || 0})} />
              </div>
            ))}
            <Button onClick={bulkCreateRooms} className="w-full">إنشاء الغرف</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Assign to Room Dialog */}
      <AssignToRoomDialog open={showAssign} onClose={() => setShowAssign(false)} 
                          room={selectedRoom} unassigned={unassignedPilgrims} onAssign={assignToRoom} />
    </div>
  );
};

// ============ ASSIGN TO ROOM DIALOG ============
const AssignToRoomDialog = ({ open, onClose, room, unassigned, onAssign }) => {
  const [selectedIds, setSelectedIds] = useState([]);

  const togglePilgrim = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssign = () => {
    if (selectedIds.length === 0) return;
    const maxAllowed = (room?.capacity || 4) - (room?.current_occupancy || 0);
    if (selectedIds.length > maxAllowed) {
      alert(`يمكن إضافة ${maxAllowed} حاج فقط`);
      return;
    }
    onAssign(room.id, selectedIds);
    setSelectedIds([]);
  };

  const maxSlots = room ? room.capacity - room.current_occupancy : 0;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>إضافة حجاج للغرفة {room?.room_number} (متاح: {maxSlots})</DialogTitle>
        </DialogHeader>
        <div className="max-h-64 overflow-y-auto border rounded-md p-2 space-y-1">
          {unassigned.map(p => (
            <label key={p.id} className={`flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-gray-50 ${selectedIds.includes(p.id) ? 'bg-brand-50' : ''}`}>
              <input type="checkbox" checked={selectedIds.includes(p.id)} onChange={() => togglePilgrim(p.id)} 
                     disabled={!selectedIds.includes(p.id) && selectedIds.length >= maxSlots} />
              <span className="text-sm">{p.first_name} {p.last_name}</span>
              <div className="mr-auto flex gap-1">
                <Badge variant="outline" className="text-xs">{p.gender === 'male' ? 'ذ' : 'أ'}</Badge>
                {p.family_name && <Badge className="bg-purple-50 text-purple-700 text-xs">{p.family_name}</Badge>}
              </div>
            </label>
          ))}
          {unassigned.length === 0 && <p className="text-center py-4 text-gray-500 text-sm">لا يوجد حجاج غير مسكّنين</p>}
        </div>
        <Button onClick={handleAssign} className="w-full" disabled={selectedIds.length === 0}>
          تعيين ({selectedIds.length}) حاج
        </Button>
      </DialogContent>
    </Dialog>
  );
};


// ============ BOOKINGS TAB (Organized Trips linked to this group) ============
const BookingsTab = ({ trips, selectedTrip, setSelectedTrip }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchBookings = useCallback(async () => {
    if (!selectedTrip) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/umrah/trips/${selectedTrip.id}/bookings`, { headers: getAuthHeaders() });
      setBookings(r.data || []);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  }, [selectedTrip]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  if (!selectedTrip) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-gray-500">
          <FileText className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p>اختر فوجاً من تبويب "الأفواج" لعرض الرحلات المنظمة المرتبطة به</p>
          {trips.length > 0 && (
            <select
              value=""
              onChange={(e) => { const t = trips.find(x => x.id === e.target.value); if (t) setSelectedTrip(t); }}
              className="mt-3 p-2 border rounded-md"
            >
              <option value="">-- اختر فوج --</option>
              {trips.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          )}
        </CardContent>
      </Card>
    );
  }

  const totalRevenue = bookings.reduce((s, b) => s + (b.final_amount || 0), 0);
  const totalPaid = bookings.reduce((s, b) => s + (b.paid_amount || 0), 0);
  const totalRemaining = totalRevenue - totalPaid;

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold">الرحلات المنظمة لفوج: {selectedTrip.name}</h2>
              <p className="text-xs text-gray-500 mt-1">
                إجمالي الحجوزات: <strong>{bookings.length}</strong>
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={fetchBookings}>
              <RefreshCw className="h-3 w-3 ml-1" /> تحديث
            </Button>
          </div>
          {bookings.length > 0 && (
            <div className="grid grid-cols-3 gap-3 mt-4">
              <div className="bg-brand-50 rounded p-3">
                <p className="text-xs text-brand-700">الإيرادات الكلية</p>
                <p className="text-xl font-bold text-brand-800 font-mono">{totalRevenue.toLocaleString()} دج</p>
              </div>
              <div className="bg-green-50 rounded p-3">
                <p className="text-xs text-green-700">المدفوع</p>
                <p className="text-xl font-bold text-green-800 font-mono">{totalPaid.toLocaleString()} دج</p>
              </div>
              <div className="bg-amber-50 rounded p-3">
                <p className="text-xs text-amber-700">المتبقي</p>
                <p className="text-xl font-bold text-amber-800 font-mono">{totalRemaining.toLocaleString()} دج</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="py-8 text-center text-gray-500">جاري التحميل...</CardContent></Card>
      ) : bookings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-gray-500">
            <FileText className="h-10 w-10 mx-auto mb-3 text-gray-300" />
            <p className="mb-1">لا توجد رحلات منظمة مرتبطة بهذا الفوج بعد</p>
            <p className="text-xs">عند إنشاء رحلة منظمة جديدة، اربطها بهذا الفوج لتظهر هنا</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="p-3 text-right">رقم الحجز</th>
                  <th className="p-3 text-right">العميل</th>
                  <th className="p-3 text-right">الهاتف</th>
                  <th className="p-3 text-right">الخدمة</th>
                  <th className="p-3 text-right">المجموع</th>
                  <th className="p-3 text-right">المدفوع</th>
                  <th className="p-3 text-right">المتبقي</th>
                  <th className="p-3 text-right">الحالة</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map(b => {
                  const remaining = (b.final_amount || 0) - (b.paid_amount || 0);
                  return (
                    <tr key={b.id} className="border-t hover:bg-gray-50">
                      <td className="p-3 font-mono text-xs">{b.trip_no}</td>
                      <td className="p-3 font-medium">{b.client_name}</td>
                      <td className="p-3 font-mono text-xs">{b.client_phone || '—'}</td>
                      <td className="p-3 text-xs">{b.service_name}</td>
                      <td className="p-3 font-mono">{(b.final_amount || 0).toLocaleString()}</td>
                      <td className="p-3 font-mono text-green-700">{(b.paid_amount || 0).toLocaleString()}</td>
                      <td className="p-3 font-mono text-amber-700">{remaining.toLocaleString()}</td>
                      <td className="p-3">
                        <Badge variant={remaining === 0 ? 'default' : 'outline'}>
                          {remaining === 0 ? 'مدفوع كاملاً' : b.payment_status || 'مستحق'}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============ LOGS TAB ============
const LogsTab = ({ trips, selectedTrip }) => {
  const [logs, setLogs] = useState([]);
  const [tripFilter, setTripFilter] = useState(selectedTrip?.id || '');

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (tripFilter) params.append('trip_id', tripFilter);
      params.append('limit', '100');
      const res = await axios.get(`${API}/umrah/activity-logs?${params}`, { headers: getAuthHeaders() });
      setLogs(res.data);
    } catch (e) { console.error(e); }
  }, [tripFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const getActionLabel = (action) => {
    const labels = {
      'create': 'إنشاء', 'update': 'تعديل', 'delete': 'حذف',
      'allocate_seats': 'تخصيص مقاعد', 'transfer': 'نقل',
      'assign_room': 'تعيين غرفة', 'auto_allocate': 'تسكين تلقائي'
    };
    return labels[action] || action;
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-center gap-4">
        <Select value={tripFilter || "all"} onValueChange={v => setTripFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-64"><SelectValue placeholder="جميع الأفواج" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">جميع الأفواج</SelectItem>
            {trips.filter(t => t && t.id).map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">التاريخ</TableHead>
                <TableHead className="text-right">المستخدم</TableHead>
                <TableHead className="text-right">الإجراء</TableHead>
                <TableHead className="text-right">التفاصيل</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {logs.map(log => (
                <TableRow key={log.id}>
                  <TableCell className="text-xs">{new Date(log.created_at).toLocaleString('ar-DZ')}</TableCell>
                  <TableCell>{log.user_name}</TableCell>
                  <TableCell><Badge variant="outline">{getActionLabel(log.action)}</Badge></TableCell>
                  <TableCell className="text-sm">{log.details}</TableCell>
                </TableRow>
              ))}
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center py-8 text-gray-500">لا توجد سجلات</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UmrahGroupManagement;
